import {
  type FnName,
  type Node,
  numValue,
  variablesOf,
} from "@/lib/tool-kit/calculus/ast";
import { simplify } from "@/lib/tool-kit/calculus/simplify";

/**
 * Numeric evaluation of an expression tree, always in radians.
 *
 * NaN or ±Infinity mean the expression leaves its domain at that point (ln of a
 * non-positive number, division by zero, asin outside [−1, 1], an even root of a
 * negative). Callers test `Number.isFinite`. A real odd root of a negative number is
 * real: (−8)^(1/3) = −2, because the exponent is an exact fraction with an odd
 * denominator.
 */

export type Env = Readonly<Record<string, number>>;

function applyFunction(fn: FnName, x: number): number {
  switch (fn) {
    case "sin":
      return Math.sin(x);
    case "cos":
      return Math.cos(x);
    case "tan":
      return Math.tan(x);
    case "sec":
      return 1 / Math.cos(x);
    case "csc":
      return 1 / Math.sin(x);
    case "cot":
      return 1 / Math.tan(x);
    case "asin":
      return Math.asin(x);
    case "acos":
      return Math.acos(x);
    case "atan":
      return Math.atan(x);
    case "sinh":
      return Math.sinh(x);
    case "cosh":
      return Math.cosh(x);
    case "tanh":
      return Math.tanh(x);
    case "ln":
      return Math.log(x);
    case "log":
      return Math.log10(x);
    default:
      return Math.abs(x);
  }
}

/** x^(n/d) with the real root for an odd denominator. */
function rationalPower(x: number, n: number, d: number): number {
  if (d === 1) {
    return x ** n;
  }
  if (x < 0 && d % 2 === 1) {
    const root = d === 3 ? Math.cbrt(-x) : (-x) ** (1 / d);
    const magnitude = root ** n;
    return n % 2 === 0 ? magnitude : -magnitude;
  }
  return x ** (n / d);
}

/** Compiled form: a function of up to two variables, `a` and `b`. */
export type Compiled = (a: number, b: number) => number;

interface Slots {
  a: string;
  b: string | null;
  fixed: Env;
}

function compileNode(node: Node, slots: Slots): Compiled {
  switch (node.kind) {
    case "num": {
      const value = numValue(node);
      return () => value;
    }
    case "const": {
      const value = node.name === "pi" ? Math.PI : Math.E;
      return () => value;
    }
    case "var": {
      if (node.name === slots.a) {
        return (a) => a;
      }
      if (node.name === slots.b) {
        return (_a, b) => b;
      }
      const fixed = slots.fixed[node.name] ?? Number.NaN;
      return () => fixed;
    }
    case "add": {
      const terms = node.terms.map((t) => compileNode(t, slots));
      return (a, b) => {
        let sum = 0;
        for (const term of terms) {
          sum += term(a, b);
        }
        return sum;
      };
    }
    case "mul": {
      const factors = node.factors.map((f) => compileNode(f, slots));
      return (a, b) => {
        let product = 1;
        for (const factor of factors) {
          product *= factor(a, b);
        }
        return product;
      };
    }
    case "pow":
      return compilePower(node, slots);
    default: {
      const arg = compileNode(node.arg, slots);
      const fn = node.fn;
      return (a, b) => applyFunction(fn, arg(a, b));
    }
  }
}

function compilePower(
  node: Extract<Node, { kind: "pow" }>,
  slots: Slots
): Compiled {
  if (node.base.kind === "const" && node.base.name === "e") {
    const exponent = compileNode(node.exp, slots);
    return (a, b) => Math.exp(exponent(a, b));
  }
  const base = compileNode(node.base, slots);
  // A constant exponent such as 1/3 is folded to an exact fraction, so (−8)^(1/3) is −2.
  const folded =
    variablesOf(node.exp).length === 0 ? simplify(node.exp) : node.exp;
  if (folded.kind === "num" && Number.isInteger(folded.n)) {
    const { n, d } = folded;
    return (a, b) => rationalPower(base(a, b), n, d);
  }
  const exponent = compileNode(folded, slots);
  return (a, b) => base(a, b) ** exponent(a, b);
}

/** A function of one variable; any other variable reads from `fixed` (NaN if absent). */
export function compile(
  node: Node,
  name = "x",
  fixed: Env = {}
): (x: number) => number {
  const compiled = compileNode(node, { a: name, b: null, fixed });
  return (x) => compiled(x, Number.NaN);
}

/** A function of two variables, e.g. f(x, y) for a differential equation. */
export function compile2(
  node: Node,
  first = "x",
  second = "y",
  fixed: Env = {}
): (x: number, y: number) => number {
  return compileNode(node, { a: first, b: second, fixed });
}

/** One-off evaluation with every variable supplied in `env`. */
export function evaluate(node: Node, env: Env = {}): number {
  const names = Object.keys(env);
  const first = names[0] ?? "x";
  const second = names[1] ?? null;
  const compiled = compileNode(node, { a: first, b: second, fixed: env });
  return compiled(env[first] ?? Number.NaN, second ? env[second] : Number.NaN);
}
